/** Prefer the app main column; fall back to nearest vertically scrollable ancestor. */
export function getVerticalScrollParent(el: HTMLElement): HTMLElement | null {
  const marked = el.closest('[data-app-scroll-root]')
  if (marked instanceof HTMLElement) return marked

  let parent = el.parentElement
  while (parent != null) {
    const { overflowY } = window.getComputedStyle(parent)
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      parent.scrollHeight > parent.clientHeight + 1
    ) {
      return parent
    }
    parent = parent.parentElement
  }
  return null
}

/**
 * Scroll `el` so its top sits `marginTop` px below the scrollport top.
 * Prefer this over `scrollIntoView` when sticky headers make native scrollIntoView a no-op.
 */
export function scrollElementToOffset(
  el: HTMLElement,
  marginTop = 0,
  behavior: ScrollBehavior = 'smooth'
): void {
  const scrollParent = getVerticalScrollParent(el)
  if (scrollParent != null) {
    const delta =
      el.getBoundingClientRect().top - scrollParent.getBoundingClientRect().top - marginTop
    if (Math.abs(delta) < 2) return
    scrollParent.scrollTo({
      top: Math.max(0, scrollParent.scrollTop + delta),
      behavior,
    })
    return
  }
  el.scrollIntoView({ behavior, block: 'start' })
}

/** Double-rAF then scroll; optional follow-up after layout/animation settles. */
export function scheduleScrollElementToOffset(
  el: HTMLElement | null | undefined,
  marginTop = 0,
  options?: { behavior?: ScrollBehavior; retryMs?: number }
): void {
  if (el == null) return
  const behavior = options?.behavior ?? 'smooth'
  const retryMs = options?.retryMs

  const run = (): void => scrollElementToOffset(el, marginTop, behavior)

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(run)
  })

  if (retryMs != null && retryMs > 0) {
    window.setTimeout(run, retryMs)
  }
}

export type ScheduleScrollAfterLayoutSettleOptions = {
  settleMs?: number
  maxWaitMs?: number
  behavior?: ScrollBehavior
  /** After the settled smooth scroll, snap again (instant) to correct late sibling layout shifts. */
  correctAfterMs?: number
  /** Extra nodes to observe (e.g. target section + accounts list). */
  alsoObserve?: Array<HTMLElement | null | undefined>
}

/**
 * Scroll after layout stops changing (tab content / sibling expanded panels).
 * Returns a cancel function for rapid tab switches / unmount.
 */
export function scheduleScrollAfterLayoutSettle(
  el: HTMLElement | null | undefined,
  marginTop = 0,
  observeRoot?: HTMLElement | null,
  options?: ScheduleScrollAfterLayoutSettleOptions
): () => void {
  if (el == null) return () => undefined

  const settleMs = options?.settleMs ?? 250
  const maxWaitMs = options?.maxWaitMs ?? 3000
  const behavior = options?.behavior ?? 'smooth'
  const correctAfterMs = options?.correctAfterMs ?? 400
  const root = observeRoot ?? el

  let settledTimer: number | undefined
  let maxTimer: number | undefined
  let correctTimer: number | undefined
  let done = false
  let observer: ResizeObserver | undefined

  const cleanup = (): void => {
    if (settledTimer != null) window.clearTimeout(settledTimer)
    if (maxTimer != null) window.clearTimeout(maxTimer)
    if (correctTimer != null) window.clearTimeout(correctTimer)
    observer?.disconnect()
  }

  const finish = (): void => {
    if (done) return
    // Keep observing through the smooth scroll + correction window so late
    // sibling resizes (sync tabs / multi-expand) can bump settle again.
    if (settledTimer != null) window.clearTimeout(settledTimer)
    settledTimer = undefined
    if (maxTimer != null) {
      window.clearTimeout(maxTimer)
      maxTimer = undefined
    }

    scrollElementToOffset(el, marginTop, behavior)

    if (correctTimer != null) window.clearTimeout(correctTimer)
    correctTimer = window.setTimeout(() => {
      if (done) return
      done = true
      cleanup()
      // Instant snap in case smooth scroll raced a layout shift.
      scrollElementToOffset(el, marginTop, 'auto')
    }, correctAfterMs)
  }

  const bumpSettle = (): void => {
    if (done) return
    if (correctTimer != null) {
      window.clearTimeout(correctTimer)
      correctTimer = undefined
    }
    if (settledTimer != null) window.clearTimeout(settledTimer)
    settledTimer = window.setTimeout(finish, settleMs)
  }

  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(() => bumpSettle())
    observer.observe(root)
    observer.observe(el)
    for (const node of options?.alsoObserve ?? []) {
      if (node != null && node !== root && node !== el) observer.observe(node)
    }
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(bumpSettle)
  })
  maxTimer = window.setTimeout(finish, maxWaitMs)

  return () => {
    done = true
    cleanup()
  }
}
